import { fetchEventSource } from '@microsoft/fetch-event-source';
import { Observable } from 'rxjs';
import { API_URL } from '@/constants/api-url.constant';

const DEFAULT_RETRY_MS = 1000;
/** Two missed server heartbeats (15s each) before forcing a reconnect. */
const IDLE_TIMEOUT_MS = 25_000;
const IDLE_CHECK_MS = 5_000;
const EVENT_STREAM_CONTENT_TYPE = 'text/event-stream';

export type SseConnectionState = 'connected' | 'reconnecting';

export interface ServerSentEventOpenOptions {
  onConnectionStateChange?: (state: SseConnectionState) => void;
}

export class ServerSentEventService<T> {
  endpoint: string;
  private activeAbortController: AbortController | null = null;

  constructor(endpoint: string) {
    this.endpoint = endpoint;
  }

  close() {
    this.activeAbortController?.abort();
    this.activeAbortController = null;
  }

  open(options?: ServerSentEventOpenOptions): Observable<T> {
    return new Observable((observer) => {
      const abortController = new AbortController();
      this.activeAbortController = abortController;
      let disposed = false;
      let hasConnectedOnce = false;

      const setState = (state: SseConnectionState) => {
        if (!disposed) {
          options?.onConnectionStateChange?.(state);
        }
      };

      const wait = (ms: number) =>
        new Promise<void>((resolve) => {
          const timeoutId = window.setTimeout(() => {
            abortController.signal.removeEventListener('abort', onAbort);
            resolve();
          }, ms);
          const onAbort = () => {
            window.clearTimeout(timeoutId);
            abortController.signal.removeEventListener('abort', onAbort);
            resolve();
          };
          abortController.signal.addEventListener('abort', onAbort, { once: true });
        });

      const run = async () => {
        const endpoint = this.endpoint;
        while (!disposed && !abortController.signal.aborted) {
          if (hasConnectedOnce) {
            setState('reconnecting');
          }

          const iterationController = new AbortController();
          const abortIteration = () => iterationController.abort();
          abortController.signal.addEventListener('abort', abortIteration, { once: true });

          let lastActivity = Date.now();
          const touchActivity = () => {
            lastActivity = Date.now();
          };

          const idleWatchdog = window.setInterval(() => {
            if (disposed || iterationController.signal.aborted) return;
            const idleMs = Date.now() - lastActivity;
            if (idleMs >= IDLE_TIMEOUT_MS) {
              console.warn(
                `[SSE] Reconnecting after idle timeout (${idleMs}ms without activity): ${endpoint}`,
              );
              setState('reconnecting');
              iterationController.abort();
            }
          }, IDLE_CHECK_MS);

          try {
            await fetchEventSource(API_URL.value + endpoint, {
              credentials: 'include',
              signal: iterationController.signal,
              openWhenHidden: true,
              onopen: async (response) => {
                const contentType = response.headers.get('content-type');
                if (!contentType?.startsWith(EVENT_STREAM_CONTENT_TYPE)) {
                  throw new Error(
                    `Expected content-type ${EVENT_STREAM_CONTENT_TYPE}, got ${contentType}`,
                  );
                }
                if (!response.ok) {
                  throw new Error(`SSE connection failed: ${response.status}`);
                }
                touchActivity();
                hasConnectedOnce = true;
                setState('connected');
              },
              onmessage(ev) {
                touchActivity();
                if (!ev.data) return;
                try {
                  observer.next(JSON.parse(ev.data) as T);
                  hasConnectedOnce = true;
                  setState('connected');
                } catch (err) {
                  console.warn(`[SSE] Ignoring malformed event on ${endpoint}:`, err);
                }
              },
              onerror(err) {
                if (disposed || abortController.signal.aborted) {
                  throw err;
                }
                setState('reconnecting');
                return DEFAULT_RETRY_MS;
              },
              onclose() {
                if (!disposed && !abortController.signal.aborted) {
                  setState('reconnecting');
                }
              },
            });
          } catch (err) {
            if (disposed || abortController.signal.aborted) {
              return;
            }
            if (hasConnectedOnce) {
              setState('reconnecting');
            }
          } finally {
            window.clearInterval(idleWatchdog);
            abortController.signal.removeEventListener('abort', abortIteration);
          }

          if (disposed || abortController.signal.aborted) {
            return;
          }

          await wait(DEFAULT_RETRY_MS);
        }
      };

      void run();

      return () => {
        disposed = true;
        abortController.abort();
        if (this.activeAbortController === abortController) {
          this.activeAbortController = null;
        }
      };
    });
  }
}
